/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import { useMutation } from '@tanstack/react-query';
import React, { FC, useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Redirect, Switch, Route, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import Maintenance from 'src/pages/maintenance';
import AdvantageModal from 'src/pages/orders/components/cargo/components/advantage-modal';
import { RootState, useAppDispatch, useAppSelector } from 'src/services/redux/app/Store';
import { announcementUnRead } from 'src/services/redux/slices/announcement/announcement-slice';
import { _login } from 'src/services/redux/slices/auth/auth-slice';
import { MasterLayout } from '../_metronic/layout/MasterLayout';
import { Logout, AuthPage } from '../modules/auth';
import { PrivateRoutes } from './PrivateRoutes';

const Routes: FC = () => {
  const dispatch = useAppDispatch();
  const { maintenance } = useAppSelector((state) => state.maintenanceList, shallowEqual);
  const auth: any = useSelector<RootState>((state) => state.auth, shallowEqual);
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const refreshToken = searchParams.get('r');
  const PLATFORM = process.env.REACT_APP_PLATFORM;
  const [showAdvantageModal, setShowAdvantageModal] = useState(false);

  const refreshTokenMutate = useMutation({
    mutationKey: ['refresh-token'],
    mutationFn: () => {
      return axios.post(`${process.env.REACT_APP_API_AUTH_URL}/login-with-refresh`, { platform: PLATFORM, refreshToken });
    },
    onSuccess: (data: any) => {
      const searchParams = new URLSearchParams(history.location.search);
      const redirect = searchParams.get('redirect');
      dispatch(_login(data.data));
      dispatch(announcementUnRead());
      if (redirect && redirect !== 'logout') history.push(`/${redirect}`);
    },
    onError: () => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('r');
      const search = searchParams.toString();
      history.push({ pathname: '/auth/login', search: search ? `?${search}` : '' });
      toast.error('Giriş başarısız.');
    },
  });

  useEffect(() => {
    if (auth?.user && !history.location.pathname.startsWith('/auth')) {
      const lastShown = localStorage.getItem('advantage_modal_shown');

      if (!lastShown) {
        setShowAdvantageModal(true);
      } else {
        const lastShownDate = new Date(lastShown);
        const oneDayInMs = 24 * 60 * 60 * 1000;
        const now = new Date();

        if (now.getTime() - lastShownDate.getTime() > oneDayInMs) {
          localStorage.removeItem('advantage_modal_shown');
          setShowAdvantageModal(true);
        }
      }
    } else {
      setShowAdvantageModal(false);
    }
  }, [auth?.user, history.location.pathname]);

  if (refreshToken && !refreshTokenMutate.isLoading && !refreshTokenMutate.isSuccess && !auth?.user) refreshTokenMutate.mutate();

  if (!maintenance) {
    return (
      <Switch>
        <Route>
          <Maintenance />
        </Route>
      </Switch>
    );
  }
  return (
    <>
      <Switch>
        {!auth?.user ? (
          /*Render auth page when user at `/auth` and not authorized.*/
          <Route>
            <AuthPage />
          </Route>
        ) : (
          /*Otherwise redirect to root page (`/`)*/
          <Redirect from="/auth" to="/" />
        )}

        <Route path="/logout" component={Logout} />

        {!auth?.user ? (
          /*Redirect to `/auth` when user is not authorized*/
          <Redirect to="/auth/login" />
        ) : (
          <MasterLayout>
            <PrivateRoutes />
            <AdvantageModal show={showAdvantageModal} handleClose={() => setShowAdvantageModal(false)} />
          </MasterLayout>
        )}
      </Switch>
    </>
  );
};

export { Routes };
